/**
 * حدود الزوم — مطابقة أرقام Zoom Earth الحرفية (من الـ bundle المفكّك app.097cc41d.js):
 *   Mi = { min: 3 - s, max: 11, home: 6, ... }  حيث s=1 للشاشات الصغيرة
 *   والشاشة "الصغيرة" عندهم: العرض ≤ 679px أو الارتفاع ≤ 549px (دالة Zh).
 */

/** نفس فحص Zoom Earth للشاشات الصغيرة (لا breakpoint تيلويند). */
export function isSmallZoomViewport(): boolean {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 679 || window.innerHeight <= 549;
}

/**
 * أدنى زوم مسموح — حدّ الجهاز فقط؛ القيد الحقيقي يُحسب في applyZoomLimits.
 *
 * على الشاشات الصغيرة يبقى حدّاً صريحاً (1) كما طُلب للجوال. أمّا على سطح المكتب فلا
 * حدّ سياسيّاً: نُعيد 0 ويتكفّل applyZoomLimits برفعه إلى «الزوم الذي يملأ عرض النافذة
 * وارتفاعها معاً»، وهو قيد **تقني** (دونه تظهر نسخ العالم مكرّرة أفقياً) لا قرار منتج.
 */
export function getMinZoom(): number {
    return isSmallZoomViewport() ? 1 : 0;
}

/**
 * أقصى زوم مسموح — 11 على كل الأجهزة، وهو رقم Zoom Earth الحرفي.
 *
 * كان 22 على سطح المكتب (سقف MapLibre) بعد أن سقط مبرّر خفضه الأصلي (ساحل landmask
 * المضلّع، وقد أُزيل). لكنّ 22 يعني تكبيراً إلى مستوى المبنى فوق حقل جوّي دقّته
 * 13-28 كم: لا معلومة طقس جديدة وراء ~11، فقط خريطة أساس تكبر بلا سبب. أُعيد التوحيد
 * على 11 مطابقةً لـ Zoom Earth كما هو مطلوب. لاستعادة رؤية الشوارع عن قرب على سطح
 * المكتب: أعِد `isSmallZoomViewport() ? 11 : 22`.
 */
export function getMaxZoom(): number {
    return 11;
}

/** @deprecated استخدم getMaxZoom() — الحدّ صار يعتمد على حجم الشاشة. */
export const MAX_ZOOM = 11;

/**
 * المدى الجغرافي المستهدف عند الفتح، بدرجات الطول (~28° ≈ 3100 كم).
 *
 * يُظهر البلد ومحيطه الإقليمي — الجزيرة العربية والخليج والشام ومصر في مشهد واحد —
 * وهو ما يُفيد في خريطة طقس: النظام الجوي يمتدّ آلاف الكيلومترات، فالفتح على مدينة
 * واحدة يُخفي الجبهة القادمة إليها. الرقم 20° كان يعطي 2220 كم فقط، أي أوسع بـ26%
 * من السابق على سطح المكتب — تحسّن غير محسوس. 28° يُعطي خطوة واضحة للخلف.
 */
const HOME_SPAN_DEG = 28;

/**
 * الزوم المبدئي عند الفتح على موقع المستخدم — **محسوب من عرض الشاشة** لا ثابتاً.
 *
 * كان ثابتاً عند 6، وهو رقم Zoom Earth، لكن الزوم الواحد يعني تغطية مختلفة تماماً
 * باختلاف عرض النافذة: عند 6 يعرض سطح مكتب 1440 بكسل نحو **1756 كم**، بينما يعرض
 * هاتف 390 بكسل **476 كم** فقط — أي محافظة واحدة. ولهذا بدا الفتح "قريباً جداً".
 *
 * الحساب هنا يعكس المعادلة: نختار الزوم الذي يجعل عرض النافذة يساوي HOME_SPAN_DEG،
 * فتتساوى التغطية على كل الأجهزة (~2200 كم) بدل أن تتساوى الأرقام وتختلف الرؤية.
 * النتيجة عملياً: ~5.7 على سطح المكتب و~3.8 على الهاتف.
 */
export function getHomeZoom(): number {
    const width = typeof window === 'undefined' ? 1280 : window.innerWidth;
    const z = Math.log2((width / 512) * (360 / HOME_SPAN_DEG));
    return Math.max(getMinZoom(), Math.min(getMaxZoom(), z));
}

/** @deprecated استخدم getHomeZoom() — الزوم المبدئي صار يتبع عرض الشاشة. */
export const HOME_ZOOM = 6;
